'use client';

import { Button, Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle, SheetTrigger } from '@shared/components/shadcn';
import { HamburguerMenuClassic } from '@shared/icons';
import { useTranslations } from 'next-intl';
import { useRef, useState } from 'react';
import { NotificationDot } from '../NotificationDot/NotificationDot';
import { HamburgerMenuItem, type HamburgerMenuItemProps, HamburgerMenuItems } from './components';

type HamburgerMenuProps = {
  topMenuItems?: Omit<HamburgerMenuItemProps, 'onMenuItemClick'>[];
  bottomMenuItems?: Omit<HamburgerMenuItemProps, 'onMenuItemClick'>[];
  namespace: string;
  hasNotifications: boolean;
};

function HamburgerMenu({ topMenuItems = [], bottomMenuItems, namespace, hasNotifications }: HamburgerMenuProps) {
  const t = useTranslations(namespace);
  const [isOpen, setIsOpen] = useState(false);
  const firstItemRef = useRef<HTMLAnchorElement | null>(null);

  const handleMenuItemClick = () => {
    setIsOpen(false);
  };

  return (
    <Sheet open={isOpen} onOpenChange={setIsOpen}>
      <SheetTrigger asChild>
        <Button
          data-testid="hamburger-menu-button"
          variant="ghost"
          size="icon-m"
          className="hover:bg-container relative"
          aria-label={t('burger_menu_button_name')}
          data-track-name="Menu"
        >
          <HamburguerMenuClassic size="medium" />
          {hasNotifications && <NotificationDot notificationSeverity="info" className="absolute top-2 right-2" data-testid="hamburger-menu-notification-dot" />}
        </Button>
      </SheetTrigger>
      <SheetContent
        className="flex flex-col gap-4 p-5 bg-background"
        data-testid="hamburger-menu-sheet-content"
        data-track-container="Burger Menu"
        onOpenAutoFocus={event => {
          event.preventDefault();
          firstItemRef.current?.focus();
        }}
      >
        <SheetHeader className="-mb-10">
          <SheetTitle className="hidden">{t('menu_title')}</SheetTitle>
          <SheetDescription className="hidden">{t('menu_description')}</SheetDescription>
        </SheetHeader>
        <HamburgerMenuItems items={topMenuItems} onMenuItemClick={handleMenuItemClick} firstItemRef={firstItemRef} />
        <HamburgerMenuItems items={bottomMenuItems} onMenuItemClick={handleMenuItemClick} divider={true} />
      </SheetContent>
    </Sheet>
  );
}

export { HamburgerMenu, HamburgerMenuItem, type HamburgerMenuItemProps };
